import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { TreePalm, Clock, Building2, Plus } from 'lucide-react'
import { amenityApi } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { stripHtml } from '../utils/htmlHelpers'
import { AMENITY_TYPE_LABELS, AMENITY_TYPE_BADGE_VARIANTS } from '../utils/amenityConstants'
import {
  PageHeader,
  SearchBar,
  Pagination,
  LoadingSpinner,
  EmptyState,
  Button,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  Badge,
  ListContainer
} from '../components/common'

const Amenities = () => {
  const { selectedBuildingId } = useAuth()
  const [amenities, setAmenities] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  const pageSize = 10

  const loadAmenities = async () => {
    if (!selectedBuildingId) return
    try {
      setLoading(true)
      const response = await amenityApi.getPaged(selectedBuildingId, {
        pageNumber: currentPage,
        pageSize,
        ...(searchTerm && { searchString: searchTerm }),
      })
      const data = response.data?.data || response.data
      const items = data?.items || data || []
      setAmenities(Array.isArray(items) ? items : [])
      setTotalPages(data?.totalPages || 1)
      setTotalCount(data?.totalCount || (Array.isArray(items) ? items.length : 0))
    } catch (error) {
      console.error('Error loading amenities:', error)
      setAmenities([])
      setTotalPages(1)
      setTotalCount(0)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (selectedBuildingId) {
      loadAmenities()
    } else {
      setAmenities([])
      setLoading(false)
    }
  }, [selectedBuildingId, currentPage, searchTerm])

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInput !== searchTerm) {
        setSearchTerm(searchInput)
        setCurrentPage(1)
      }
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [searchInput, searchTerm])

  const handleSearch = (term) => {
    setSearchInput(term)
  }

  const handleSearchSubmit = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      setSearchTerm(searchInput)
      setCurrentPage(1)
    }
  }

  const formatTime = (time) => {
    if (!time) return 'N/A'
    return time.substring(0, 5)
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Amenities"
        subtitle={`${totalCount} amenities found`}
        action={selectedBuildingId ? (
          <Button
            as={Link}
            to={`/amenities/create?buildingId=${selectedBuildingId}`}
            variant="primary"
            icon={<Plus className="w-4 h-4" />}
          >
            Create Amenity
          </Button>
        ) : null}
      />

      {selectedBuildingId && (
        <SearchBar
          value={searchInput}
          onChange={handleSearch}
          onKeyDown={handleSearchSubmit}
          placeholder="Search amenities... (Press Enter to search)"
        />
      )}

      {!selectedBuildingId ? (
        <EmptyState
          icon={Building2}
          title="Select a building"
          description="Choose a building from the top bar to view its amenities."
        />
      ) : loading ? (
        <LoadingSpinner />
      ) : amenities.length === 0 ? (
        <EmptyState
          icon={TreePalm}
          title="No amenities found"
          description="This building has no amenities configured."
        />
      ) : (
        <ListContainer>
          <div className="space-y-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableCell header>Name</TableCell>
                  <TableCell header>Type</TableCell>
                  <TableCell header>Available Hours</TableCell>
                  <TableCell header>Roles</TableCell>
                  <TableCell header>Bookings</TableCell>
                  <TableCell header>
                    <span className="sr-only">Actions</span>
                  </TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {amenities.map((amenity) => (
                  <TableRow key={amenity.amenityId} clickable>
                    <TableCell>
                      <div className="flex items-center">
                        <div className="p-2 bg-primary-100/50 rounded-lg mr-3 shadow-sm">
                          <TreePalm className="w-5 h-5 text-primary-600" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-slate-900 group-hover:text-primary-600 transition-colors">
                            {amenity.name || 'Unnamed Amenity'}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={AMENITY_TYPE_BADGE_VARIANTS[amenity.amenityType] || 'default'} size="small">
                        {AMENITY_TYPE_LABELS[amenity.amenityType] || 'Unknown'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center text-sm text-slate-600">
                        <Clock className="w-4 h-4 mr-1 text-slate-400" />
                        {formatTime(amenity.startTime)} - {formatTime(amenity.endTime)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-slate-600">
                        {stripHtml(amenity.rolesDescription) || 'All roles'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="info" size="small">
                        {amenity.totalBookings || 0}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end pr-4">
                        <Button
                          as={Link}
                          to={`/amenities/${amenity.amenityId}?buildingId=${selectedBuildingId}`}
                          variant="ghost"
                          size="small"
                        >
                          View
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {totalPages > 1 && (
              <div className="mt-4 flex justify-between items-center px-2">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </div>
        </ListContainer>
      )}
    </div>
  )
}

export default Amenities
